import { ISheetItemRedirectRequest } from "../../api-client-nswag/create-sites-client";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { DeliverableColumns, ESignatureColumns, ESignatureStatuses, HQISheetName, IRColumns, K1TrackerColumns } from "../../constants";
import { convertUTCTimeToLocal } from "../../util/Utils";

export const STATUS_COLOR_MAP: Record<string, string> = {
    Accepted: '#8bc385',
    'Ready for Client Review': '#8bc385',
    'In Progress': '#EDE4A5',
    Outstanding: '#f6d285',
    Pending:'#f6d285',
    'Partially Provided': '#EDE4A5',
    Provided: '#B7B9BA',
    Modified: '#E88634',
    Returned: '#B85F5B',
    Complete: '#00a65a',
    'Estimated Pending': '#f6d285',
    Signed: '#8bc385',
    Voided: '#B85F5B',
    Received: '#8bc385',
    'Estimated Received': '#8bc385',
};


export function filterReportingRows(
    rows: any[],
    filters: Record<string, (row: any) => boolean>,
    excludedFilter?: string,
) {
    return rows.filter((row) => Object.entries(filters).every(([filterName, matches]) =>
        filterName === excludedFilter || matches(row)
    ));
}

export function getLastRefreshTimeFromRawData(rawData: any[]) {
    const lastModifiedValues = rawData
        .map((item: any) => item?.LastModified ?? item?.lastModified)
        .filter((value: any) => value !== undefined && value !== null && String(value).trim() !== '');

    if (lastModifiedValues.length === 0) {
        return '';
    }

    const latestDate = lastModifiedValues
        .map((value: any) => new Date(value))
        .filter((date: Date) => !Number.isNaN(date.getTime()))
        .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];

    if (latestDate) {
        return convertUTCTimeToLocal(latestDate.toDateString(), "MM/DD/YYYY HH:mm:ss");
    }

    return String(lastModifiedValues[0]);
}

const getPointClickFilterValue = (event: any): string => {
    const target = event?.target;
    return target?.argument
        || target?.originalArgument
        || target?.pointName
        || '';
};

const getPointClickEntityName = (event: any): string | undefined => {
    const target = event?.target;
    return target?.data?.entityName || target?.data?.entity || undefined;
};

type PointClickContext = {
    filterValue: string;
    entityName?: string;
};

const getDefaultPointClickContext = (event: any): PointClickContext => ({
    filterValue: getPointClickFilterValue(event),
    entityName: getPointClickEntityName(event),
});

const normalizeRedirectUrl = (url: string): string => {
    return url
        .replace(/%28/gi, '(')
        .replace(/%29/gi, ')');
};

const navigateToRedirect = async (request: ISheetItemRedirectRequest): Promise<void> => {
    const redirectUrl = await createSitesClientApi.dashboard_BuildRedirectUrl(request);

    if (typeof redirectUrl === 'string' && redirectUrl.trim().length > 0) {
        window.open(normalizeRedirectUrl(redirectUrl), "_blank", "noopener,noreferrer");
    } else {
        console.error('No valid redirect URL returned');
    }
};

function getK1PointContext(event: any, chartName?: string) {
    const target = event?.target;
    const seriesName = target?.data?.entityName || null;
    const fund = target?.argument || target?.originalArgument || target?.data?.fund || null;
    let secondFilter = 'No';

    if (chartName === 'Federal and State K-1 Estimates Received') {
        secondFilter = target?.data?.estimatedYes > 0 ? 'Yes' : 'No';
    } else {
        secondFilter = target?.data?.finalizedYes > 0 ? 'Yes' : 'No';
    }

    return {
        entityName: seriesName ? String(seriesName) : null,
        filterValue: fund !== undefined && fund !== null ? String(fund) : null,
        secondFilterValue: secondFilter !== undefined && secondFilter !== null ? String(secondFilter) : null,
    };
}

export const handlePointClick = async (
    event: any,
    siteId: number,
    iSheetName: string,
    buildFilters: (context: PointClickContext) => Array<{ columnName: string; filterValue: any }>,
    resolveContext: (event: any) => PointClickContext = getDefaultPointClickContext,
): Promise<void> => {
    const context = resolveContext(event);
    const filterValue = context?.filterValue || '';
    const entityName = context?.entityName;

    if (!filterValue) {
        console.warn('No filter value found on point click');
        return;
    }

    try {
        const request: ISheetItemRedirectRequest = {
            siteId,
            iSheetName,
            filters: buildFilters({ filterValue, entityName }),
        };

        await navigateToRedirect(request);
    } catch (error) {
        console.error('Error building redirect URL:', error);
    }
};

export const handleESignaturePointClick = async (event: any, siteId: number) => {
    return handlePointClick(event, siteId, HQISheetName.DOCUMENT_METADATA, ({ filterValue, entityName }) => {
        const filters: Array<{ columnName: string; filterValue: any }> = [];

        if (filterValue === 'Signed') {
            filters.push({
                columnName: ESignatureColumns.FILE_STATUS,
                filterValue: ESignatureStatuses.SIGNED,
            });
        } else if (filterValue === 'Voided') {
            filters.push({
                columnName: ESignatureColumns.FILE_STATUS,
                filterValue: ESignatureStatuses.UNSIGNED,
            });
        }

        filters.push({
            columnName: ESignatureColumns.ENTITY_NAME,
            filterValue: entityName,
        });

        return filters;
    });
};

// IR pie points are aggregated by status only, so entity must be resolved from the raw rows behind that status (same pattern as handleDeliverablesPointClick).
function resolveIREntityName(rawIRData: any[] | undefined, status: string, entityFilter?: string[]): string | undefined {
    console.log('Resolving IR entity name for status:', status, 'with entity filter:', entityFilter, rawIRData);
    const rawData = Array.isArray(rawIRData) ? rawIRData : [];
    const statusMatches = rawData.filter((item: any) => String(item?.status || 'Unknown Status') === status);
    const scoped = entityFilter && entityFilter.length > 0
        ? statusMatches.filter((item: any) => entityFilter.includes(item?.entity || item?.entityName || 'Unknown Entity'))
        : statusMatches;
    const pool = scoped.length > 0 ? scoped : statusMatches;
    const entityNames = Array.from(new Set(pool.map((item: any) => item?.entity || item?.entityName).filter(Boolean)));

    if (entityNames.length > 0) return entityNames.join(',');
    if (entityFilter && entityFilter.length > 0) return entityFilter.join(',');
    return undefined;
}

export const handleIRPointClick = async (event: any, siteId: number, entityFilter?: string[], rawIRData?: any[]) => {
    return handlePointClick(
        event,
        siteId,
        HQISheetName.INFORMATION_REQUEST,
        ({ filterValue, entityName }: PointClickContext) => [
            { columnName: IRColumns.STATUS, filterValue: String(filterValue) },
            { columnName: IRColumns.ENTITY_NAME, filterValue: entityName },
        ],
        (evt) => {
            const filterValue = getPointClickFilterValue(evt);
            return {
                filterValue,
                entityName: resolveIREntityName(rawIRData, filterValue, entityFilter),
            };
        }
    );
};

export const handleK1PointClick = async (event: any, siteId: number, chartName: string) => {
    if (!chartName) {
        console.warn('No chart name provided');
        return;
    }

    const { secondFilterValue } = getK1PointContext(event, chartName);

    return handlePointClick(event, siteId, HQISheetName.K1_TRACKER, ({ filterValue, entityName }) => [
        { columnName: K1TrackerColumns.FUND, filterValue: String(filterValue) },
        { columnName: chartName, filterValue: String(secondFilterValue) },
        { columnName: K1TrackerColumns.ENTITY_NAME, filterValue: entityName },
    ]);
};

export const handleDeliverablesPointClick = async (
    event: any,
    siteId: number,
    filterName: string = DeliverableColumns.STATUS,
    secondFilter?: string,
    rawDeliverablesData?: any[]
) => {
    const pointContext = getDeliverablesPointContext(event);

    return handlePointClick(
        event,
        siteId,
        HQISheetName.DELIVERABLES,
        ({ filterValue, entityName }) => {
            const filters: { columnName: string; filterValue: string | undefined }[] = [
                { columnName: filterName, filterValue: String(filterValue) },
                { columnName: DeliverableColumns.ENTITY_NAME, filterValue: entityName || undefined },
            ];

            if (secondFilter) {
                const rawData = Array.isArray(rawDeliverablesData)
                    ? rawDeliverablesData
                    : (event?.component?.option?.('dataSource') || []);
                const rawResolvedSecondFilterValue = getSecondFilterValueFromRawData(
                    Array.isArray(rawData) ? rawData : [],
                    filterName,
                    String(filterValue),
                    secondFilter,
                    entityName || null
                );

                const resolvedSecondFilterValue =
                    rawResolvedSecondFilterValue || pointContext.secondFilterValue || undefined;

                filters.push({
                    columnName: secondFilter,
                    filterValue: resolvedSecondFilterValue,
                });
            }

            return filters;
        },
        () => ({
            filterValue: pointContext.filterValue || '',
            entityName: pointContext.entityName || undefined,
        })
    );
};


function getDeliverablesPointContext(event: any) {
    const target = event?.target;
    const entityName =
        target?.data?.entityName ||
        target?.data?.entity ||
        target?.seriesName ||
        target?.series?.name ||
        target?.name ||
        target?.argument ||
        target?.originalArgument ||
        null;
    const argument = target?.argument || target?.originalArgument || target?.data?.fund || null;
    const secondFilterValue =
        target?.name ||
        target?.seriesName ||
        target?.series?.name ||
        target?.pointName ||
        null;
    return {
        entityName: entityName ? String(entityName) : null,
        filterValue: argument !== undefined && argument !== null ? String(argument) : null,
        secondFilterValue: secondFilterValue ? String(secondFilterValue) : null,
    };
}

function getSecondFilterValueFromRawData(
    rawData: any[],
    primaryFilterName: string,
    primaryFilterValue: string,
    secondFilterName: string,
    entityName: string | null,
) {
    const exactMatch = rawData.find((item: any) =>
        matchesPrimaryFilter(item, primaryFilterName, primaryFilterValue) &&
        (!!entityName ? (item?.entity || item?.entityName || 'Unknown Entity') === entityName : true)
    );

    const exactValue = exactMatch ? getRawItemColumnValue(exactMatch, secondFilterName) : null;
    if (exactValue) return exactValue;

    const fallbackMatch = rawData.find((item: any) =>
        matchesPrimaryFilter(item, primaryFilterName, primaryFilterValue)
    );
    return fallbackMatch ? getRawItemColumnValue(fallbackMatch, secondFilterName) : null;
}
function matchesPrimaryFilter(item: any, primaryFilterName: string, primaryFilterValue: string) {
    if (primaryFilterName === DeliverableColumns.STATUS) {
        return includesStatus(item?.status, primaryFilterValue);
    }

    const primaryValue = getRawItemColumnValue(item, primaryFilterName);
    return primaryValue === primaryFilterValue;
}
function getRawItemColumnValue(item: any, columnName: string) {
    switch (columnName) {
        case DeliverableColumns.STATUS:
            return item?.status ? String(item.status) : null;
        case DeliverableColumns.FUND:
            return item?.fund ? String(item.fund) : null;
        case DeliverableColumns.FEDERAL_RETURN_TYPE:
            return item?.federalReturnType ? String(item.federalReturnType) : null;
        case DeliverableColumns.FEDERAL_DUE_DATE: {
            const rawDate = item?.federalDueDate || item?.federalReturnDueDate;
            return formatDateLabel(rawDate);
        }
        case DeliverableColumns.FEDERAL_EXTENDED_DUE_DATE: {
            const rawDate =
                item?.federalExtendedDueDate ||
                item?.federalExtendedReturnDueDate ||
                item?.extendedDueDate;
            return rawDate ? String(rawDate) : null;
        }
        default:
            return item?.[columnName] ? String(item[columnName]) : null;
    }
}
function includesStatus(itemStatus: string | undefined, selectedStatus: string) {
    return (itemStatus || '')
        .split(',')
        .map((s: string) => s.trim())
        .includes(selectedStatus);
}

function formatDateLabel(dateValue: any) {
    if (!dateValue) return null;
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime())
        ? String(dateValue)
        : parsedDate.toLocaleDateString('en-US');
}

export { convertUTCTimeToLocal };
